import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { getTours, getTourGroups } from "../Api/TourApi";
import { buildItemListSchema } from "../seo/schema";
import { normalizeTours, tourGroupKeys } from "../seo/tourSeo";
import { NAP } from "../seo/site";
import Tour from "./Tour";

const SKELETON_COUNT = 8;

/** Filter pills plus the grid of tour cards on the home page. */
const Tours = () => {
  const [groups, setGroups] = useState([]);
  const [tours, setTours] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    getTourGroups("all")
      .then((rows) => {
        if (cancelled) return;
        setGroups(
          rows
            .map((r) => r.attributes)
            .filter((g) => g && g.frontpage)
            .sort((a, b) => String(a.readable_name).localeCompare(String(b.readable_name)))
        );
      })
      .catch(() => {});
    getTours()
      .then((rows) => {
        if (cancelled) return;
        setTours(normalizeTours(rows));
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = selected ? tours.filter((t) => tourGroupKeys(t).includes(selected)) : tours;

  return (
    <>
      {tours.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(buildItemListSchema(tours))}</script>
        </Helmet>
      )}

      <div className="tour-filter" role="group" aria-label="Filter tours by category">
        <button
          type="button"
          className={`tour-filter__btn${!selected ? " tour-filter__btn--active" : ""}`}
          aria-pressed={!selected}
          onClick={() => setSelected(null)}
        >
          All Tours
        </button>
        {groups.map((group) => (
          <button
            type="button"
            key={group.name}
            className={`tour-filter__btn${selected === group.name ? " tour-filter__btn--active" : ""}`}
            aria-pressed={selected === group.name}
            onClick={() => setSelected(group.name)}
          >
            {group.readable_name}
          </button>
        ))}
      </div>

      <Container style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Row style={{ margin: 0 }} aria-live="polite">
          {status === "loading" &&
            Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <Col key={i} xl={3} lg={4} md={6} sm={6} xs={6} className="tour-card-col">
                <div className="tour-card tour-card--skeleton" aria-hidden="true">
                  <div className="skeleton__img" />
                  <div className="skeleton__body">
                    <div className="skeleton__line" />
                    <div className="skeleton__line skeleton__line--short" />
                    <div className="skeleton__btn" />
                  </div>
                </div>
              </Col>
            ))}
          {status === "error" && (
            <Col xs={12}>
              <div className="tours-error" role="alert">
                We couldn't load the tour list right now. Please refresh, or call{" "}
                <a href={NAP.telephoneHref}>{NAP.telephoneDisplay}</a> and we'll help you book.
              </div>
            </Col>
          )}
          {status === "ready" && visible.map((tour) => <Tour key={tour.pathTitle} tour={tour} />)}
          {status === "ready" && visible.length === 0 && (
            <Col xs={12}>
              <div className="tours-error">No tours in this category right now. Check back soon!</div>
            </Col>
          )}
        </Row>
      </Container>
    </>
  );
};

export default Tours;
